import axios from "axios";
import { HttpClient, ContentType } from "../apiClient/http-client";
import { Patent } from "../apiClient/Patent";
import type { CasePredictionRequest } from "../apiClient/data-contracts";
import { getValidAccessToken } from "@/lib/supabase/getValidAccessToken";
import { getRefreshToken, setTokens as storeTokens } from "@/lib/token-store";
import { createAuthHeaders } from "@/lib/auth-utils";

// Base types
interface BaseResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// Contract Review Types
interface ContractAnalysisRequest {
  file: File;
  contractType?: string;
  analysis_options?: string;
  language?: string;
  jurisdiction?: string;
}

interface ContractAnalysisResponse extends BaseResponse {
  analysis?: {
    clauses: Record<string, string>;
    risks: string[];
    recommendations: string[];
  };
}

// Legal Research Types
interface LegalResearchRequest {
  query: string;
  jurisdiction?: string;
  documentType?: string;
  researchArea?: string;
}

interface LegalResearchResponse extends BaseResponse {
  results?: {
    sources: string[];
    analysis: string;
    citations: string[];
  };
  validation?: {
    warnings: string[];
    missing_elements: string[];
  };
}

// Regulatory Analysis Types
interface RegulatoryAnalysisRequest {
  regulationText: string;
  effectiveDate: string;
  industry: string;
  businessOperations: string[];
  currentComplianceStatus: Record<string, string>;
}

interface RegulatoryAnalysisResponse extends BaseResponse {
  analysis?: {
    impact: string;
    requirements: string[];
    timeline: string[];
    resources: string[];
  };
}

interface DocumentDraftingRequest {
  document_type: string;
  jurisdiction?: string;
  key_requirements?: string[];
  parties: Record<string, string>[];
  legal_terms?: string[] | null;
  data_processing?: Record<string, string> | null;
}

interface DocumentDraftingResponse extends BaseResponse {
  document: {
    content: string;
    markdown: boolean;
    sections: string[];
  };
  document_type: string;
  jurisdiction: string;
  key_requirements: string[];
  parties: Record<string, string>[];
  colombian_compliance: {
    constitutional_principles: string[];
    data_protection: {
      law: string;
      status: string;
    };
    version: string;
  };
  legal_terms?: Record<string, string> | null;
  data_processing?: Record<string, string> | null;
  timestamp?: string;
  validation?: {
    warnings: string[];
    missing_elements: string[];
  };
}

interface LegalChatRequest {
  query: string;
  context?: string;
  language?: string;
}

interface LegalChatResponse extends BaseResponse {
  results?: {
    analysis: string;
    sources: string[];
    citations: string[];
  };
}

// Patent Search Types
interface PatentSearchRequest {
  invention_description: string;
  jurisdiction?: string;
  technical_field?: string | null;
  data_processing?: Record<string, string> | null;
  legal_terms?: string[] | null;
}

interface PatentSearchResult {
  patent_number: string;
  title: string;
  similarity: string;
  applicant: string;
  filing_date: string;
  status: string;
  classification: string;
  description: string;
  tags: string[];
}

interface PatentSearchResponse {
  search_results: PatentSearchResult[];
  invention_description: string;
  jurisdiction: string;
  colombian_compliance: Record<string, any>;
  sic_requirements: Record<string, string>;
  data_processing?: Record<string, string> | null;
  legal_terms?: Record<string, string> | null;
  timestamp?: string;
  analysis?: {
    similarityDistribution: {
      high: number;
      medium: number;
      low: number;
    };
    yearlyDistribution: number[];
    topApplicants: Array<{
      name: string;
      count: number;
    }>;
  };
  recommendations?: Array<{
    type: "warning" | "success" | "info";
    title: string;
    description: string;
  }>;
}

// Trademark Search Types (Colombia legal context)
interface TrademarkSearchRequest {
  name: string; // Nombre de la marca
  class?: string; // Clase NIZA
  type?: string; // Tipo de marca (denominativa, figurativa, mixta, tridimensional)
  status?: string; // Estado del registro (Registrada, En trámite, Abandonada)
  applicant?: string; // Titular
  description?: string; // Descripción de productos/servicios
  filing_date_from?: string;
  filing_date_to?: string;
}

interface TrademarkSearchResult {
  id: string;
  name: string;
  registrationNumber: string;
  applicant: string;
  filingDate: string;
  status: string;
  class: string;
  similarity: string;
  description: string;
  tags: string[];
  owners: string[];
  priority: string;
  representatives: string[];
  logo?: string;
  colors?: string[];
}

interface TrademarkSearchResponse {
  search_results: TrademarkSearchResult[];
  summary?: any; // For analysis sidebar
}

type RequestData =
  | FormData
  | ContractAnalysisRequest
  | LegalResearchRequest
  | RegulatoryAnalysisRequest
  | DocumentDraftingRequest
  | CasePredictionRequest
  | PatentSearchRequest
  | TrademarkSearchRequest;

class LegalChatClient extends HttpClient {
  constructor(baseUrl: string) {
    super({ baseUrl });
  }

  public async conductLegalChat(
    request: LegalChatRequest
  ): Promise<LegalChatResponse> {
    const response = await this.request<LegalChatResponse>({
      path: "/api/v1/ask",
      method: "POST",
      body: request,
      type: ContentType.Json,
    });
    return response.data;
  }
}

class LegalAIAssistantClient {
  private baseURL: string;
  private apiKey?: string;
  private patentClient: Patent;

  constructor(baseURL: string, apiKey?: string) {
    this.baseURL = baseURL;
    this.apiKey = apiKey;
    this.patentClient = new Patent({
      baseUrl: baseURL,
      securityWorker: (token) =>
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
    });
  }

  private async request<T>(
    method: "GET" | "POST",
    endpoint: string,
    data?: RequestData,
    headers: Record<string, string> = {}
  ): Promise<T> {
    try {
      let authHeader: Record<string, string> = {};
      if (!this.apiKey) {
        const token = await getValidAccessToken();
        if (token) {
          authHeader = { Authorization: `Bearer ${token}` };
        }
      }
      const refresh = getRefreshToken();
      const response = await axios({
        method,
        url: `${this.baseURL}${endpoint}`,
        data,
        headers: {
          ...headers,
          ...(this.apiKey
            ? { Authorization: `Bearer ${this.apiKey}` }
            : authHeader),
          ...(refresh ? { "X-Refresh-Token": refresh } : {}),
        },
      });
      // Update tokens if backend sent new ones in headers
      const newAccess = response.headers["x-access-token"] as
        | string
        | undefined;
      const newRefresh = response.headers["x-refresh-token"] as
        | string
        | undefined;
      if (newAccess || newRefresh) {
        storeTokens(newAccess || null, newRefresh || null);
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // If unauthorized, try refresh flow once
        if (error.response?.status === 401 && !this.apiKey) {
          try {
            const refresh = getRefreshToken();
            if (!refresh) throw error;
            const res = await axios.post(
              `${this.baseURL}/api/v1/auth/refresh`,
              { refresh_token: refresh },
              { headers: { "Content-Type": "application/json" } }
            );
            const newAccess =
              res.data?.access_token || res.headers["x-access-token"];
            const newRefresh =
              res.data?.refresh_token || res.headers["x-refresh-token"];
            if (newAccess || newRefresh) {
              storeTokens(newAccess || null, newRefresh || null);
            }
            // retry original request
            return await this.request<T>(method, endpoint, data, headers);
          } catch (e) {
            // fall through to throw
          }
        }
        throw new Error(error.response?.data?.error || error.message);
      }
      throw error;
    }
  }

  // Contract Review Methods
  async analyzeContract(
    request: ContractAnalysisRequest
  ): Promise<ContractAnalysisResponse> {
    const formData = new FormData();
    formData.append("file", request.file);
    if (request.contractType) {
      formData.append("contractType", request.contractType);
    }
    if (request.analysis_options) {
      formData.append("analysis_options", request.analysis_options);
    }
    if (request.language) {
      formData.append("language", request.language);
    }
    if (request.jurisdiction) {
      formData.append("jurisdiction", request.jurisdiction);
    }

    return this.request<ContractAnalysisResponse>(
      "POST",
      "/api/v1/contract-review/analyze",
      formData,
      { "Content-Type": "multipart/form-data" }
    );
  }

  async getContractTypes(): Promise<Record<string, string[]>> {
    return this.request<Record<string, string[]>>("GET", "/api/v1/contract-types");
  }

  // Legal Research Methods
  async conductLegalResearch(
    request: LegalResearchRequest
  ): Promise<LegalResearchResponse> {
    return this.request<LegalResearchResponse>(
      "POST",
      "/api/v1/dashboard/legal-research/investigate",
      request
    );
  }

  async getResearchAreas(): Promise<string[]> {
    return this.request<string[]>("GET", "/api/v1/dashboard/legal-research/areas");
  }

  async getDocumentTypes(): Promise<string[]> {
    return this.request<string[]>(
      "GET",
      "/api/v1/dashboard/legal-research/document-types"
    );
  }

  // Regulatory Analysis Methods
  async analyzeRegulatoryChange(
    request: RegulatoryAnalysisRequest
  ): Promise<RegulatoryAnalysisResponse> {
    return this.request<RegulatoryAnalysisResponse>(
      "POST",
      "/api/v1/regulatory/api/regulatory-analysis",
      request
    );
  }

  async getRegulatoryFrameworks(): Promise<Record<string, string[]>> {
    return this.request<Record<string, string[]>>(
      "GET",
      "/api/v1/regulatory/frameworks"
    );
  }

  // Document Drafting Methods
  async draftDocumentEndpointApiV1DraftDocumentPost(
    request: DocumentDraftingRequest
  ): Promise<DocumentDraftingResponse> {
    return this.request<DocumentDraftingResponse>(
      "POST",
      "/api/v1/draft-document",
      request
    );
  }

  async draftCustomDocumentEndpointApiV1DraftCustomDocumentPost(
    request: DocumentDraftingRequest
  ): Promise<DocumentDraftingResponse> {
    return this.request<DocumentDraftingResponse>(
      "POST",
      "/api/v1/draft-custom-document",
      request
    );
  }

  async conductLegalChat(
    request: LegalChatRequest
  ): Promise<LegalChatResponse> {
    return this.request<LegalChatResponse>(
      "POST",
      "/api/v1/ask",
      request
    );
  }

  // async analyzeCasePrediction(
  //   request: CasePredictionRequest
  // ): Promise<CasePredictionApiResult> {
  //   try {
  //     const response = await this.request<CasePredictionApiResult>(
  //       "POST",
  //       "/api/v1/dashboard/case-prediction/analyze",
  //       request,
  //       { "Content-Type": "application/json" }
  //     );

  //     if (!response) {
  //       throw new Error("No se recibió respuesta del servidor");
  //     }

  //     return response;
  //   } catch (error) {
  //     console.error("Error analyzing case:", error);
  //     if (error instanceof Error) {
  //       throw error;
  //     }
  //     throw new Error("Error al analizar el caso");
  //   }
  // }

  // Patent Search Methods
  // async searchPatents(
  //   request: PatentSearchRequest
  // ): Promise<PatentSearchResponse> {
  //   try {
  //     const response =
  //       await this.patentClient.searchPatentsEndpointPatentSearchPost(request, {
  //         headers: this.apiKey
  //           ? { Authorization: `Bearer ${this.apiKey}` }
  //           : {},
  //       });

  //     // Transform the API response to match our UI needs
  //     const transformedResponse: PatentSearchResponse = {
  //       ...response.data,
  //       search_results: response.data.search_results.map((result: any) => ({
  //         patent_number: result.patent_number,
  //         title: result.title,
  //         similarity: result.similarity,
  //         applicant: result.applicant,
  //         filing_date: result.filing_date,
  //         status: result.status,
  //         classification: result.classification,
  //         description: result.description,
  //         tags: result.tags || [],
  //       })),
  //       analysis: {
  //         similarityDistribution: {
  //           high: response.data.search_results.filter((r: any) =>
  //             r.similarity.includes("alta")
  //           ).length,
  //           medium: response.data.search_results.filter((r: any) =>
  //             r.similarity.includes("media")
  //           ).length,
  //           low: response.data.search_results.filter((r: any) =>
  //             r.similarity.includes("baja")
  //           ).length,
  //         },
  //         yearlyDistribution: this.calculateYearlyDistribution(
  //           response.data.search_results
  //         ),
  //         topApplicants: this.calculateTopApplicants(
  //           response.data.search_results
  //         ),
  //       },
  //       recommendations: this.generateRecommendations(
  //         response.data.search_results
  //       ),
  //     };

  //     return transformedResponse;
  //   } catch (error) {
  //     if (axios.isAxiosError(error)) {
  //       throw new Error(error.response?.data?.error || error.message);
  //     }
  //     throw error;
  //   }
  // }

  private calculateYearlyDistribution(results: any[]): number[] {
    // Group results by year and calculate distribution
    const yearlyCounts = results.reduce<Record<string, number>>(
      (acc, result: any) => {
        const year = new Date(result.filing_date).getFullYear();
        acc[year] = (acc[year] || 0) + 1;
        return acc;
      },
      {}
    );

    // Convert to array of percentages
    const counts = Object.values(yearlyCounts);
    if (counts.length === 0) return [];
    const maxCount = Math.max(...counts);
    return counts.map((count) => (count / maxCount) * 100);
  }

  private calculateTopApplicants(
    results: any[]
  ): Array<{ name: string; count: number }> {
    const applicantCounts = results.reduce<Record<string, number>>(
      (acc, result: any) => {
        acc[result.applicant] = (acc[result.applicant] || 0) + 1;
        return acc;
      },
      {}
    );

    return Object.entries(applicantCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }

  private generateRecommendations(results: any[]): Array<{
    type: "warning" | "success" | "info";
    title: string;
    description: string;
  }> {
    const recommendations = [];

    // Check for high similarity patents
    const highSimilarityPatents = results.filter((r) =>
      r.similarity.includes("alta")
    );
    if (highSimilarityPatents.length > 0) {
      recommendations.push({
        type: "warning",
        title: "Riesgo potencial de infracción",
        description: `La patente ${highSimilarityPatents[0].patent_number} tiene elementos muy similares a su búsqueda.`,
      });
    }

    // Check for areas with low patent activity
    const lowActivityAreas = this.identifyLowActivityAreas(results);
    if (lowActivityAreas.length > 0) {
      recommendations.push({
        type: "success",
        title: "Oportunidad de diferenciación",
        description: `Considere enfocarse en ${lowActivityAreas[0]}, un área menos cubierta.`,
      });
    }

    // Add general recommendation
    recommendations.push({
      type: "info",
      title: "Área de novedad",
      description:
        "La integración con IA para optimización parece un área poco explorada.",
    });

    return recommendations;
  }

  private identifyLowActivityAreas(results: any[]): string[] {
    // This is a simplified example - in a real implementation, you would use more sophisticated analysis
    const areas = [
      "sistema de almacenamiento",
      "optimización de energía",
      "integración IoT",
    ];
    return areas.filter(
      (area) =>
        !results.some((r) =>
          r.description.toLowerCase().includes(area.toLowerCase())
        )
    );
  }

  // TODO: Implement backend endpoint for trademark search (POST /trademark/search)
  async searchTrademarks(
    request: TrademarkSearchRequest
  ): Promise<TrademarkSearchResponse> {
    // This is a stub. Replace with actual API call when endpoint is available.
    // Example: await this.trademarkClient.searchTrademarksEndpointTrademarkSearchPost(request, ...)
    return {
      search_results: [],
      summary: {},
    };
  }

  async getUserCredits(): Promise<{
    user_id: string;
    total_balance: number;
    plan_credits_remaining: number;
    topup_credits_remaining: number;
    last_reset_at?: string;
  }> {
    const authHeaders = await createAuthHeaders();
    const res = await fetch(`${this.baseURL}/api/v1/credits/balance`, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
    });
    if (!res.ok) {
      return { user_id: "", total_balance: 120, plan_credits_remaining: 120, topup_credits_remaining: 0 };
    }
    return res.json();
  }

  async getCreditUsageLogs(limit: number = 50): Promise<Array<any>> {
    const authHeaders = await createAuthHeaders();
    const res = await fetch(`${this.baseURL}/api/v1/credits/usage?limit=${limit}`, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
    });
    if (!res.ok) return [];
    return res.json();
  }

  async checkAndDeductCredits(featureName: string): Promise<{ success: boolean; credits_deducted: number; remaining_balance: number }> {
    const authHeaders = await createAuthHeaders();
    const res = await fetch(`${this.baseURL}/api/v1/credits/check-and-deduct`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ feature_name: featureName }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Error al verificar créditos");
    }
    const data = await res.json();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("credits-updated"));
    }
    return data;
  }

  async addTopupCredits(creditsAmount: number): Promise<{
    success: boolean;
    total_balance: number;
    plan_credits_remaining: number;
    topup_credits_remaining: number;
    credits_added: number;
  }> {
    const authHeaders = await createAuthHeaders();
    const res = await fetch(`${this.baseURL}/api/v1/credits/topup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ credits_amount: creditsAmount }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Error al agregar créditos de recarga");
    }
    const data = await res.json();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("credits-updated"));
    }
    return data;
  }
}

export const createLegalAIAssistantClient = (
  baseURL: string,
  apiKey?: string
) => {
  return new LegalAIAssistantClient(baseURL, apiKey);
};

export type {
  ContractAnalysisRequest,
  ContractAnalysisResponse,
  LegalResearchRequest,
  LegalResearchResponse,
  RegulatoryAnalysisRequest,
  RegulatoryAnalysisResponse,
  DocumentDraftingRequest,
  DocumentDraftingResponse,
  PatentSearchRequest,
  PatentSearchResponse,
  PatentSearchResult,
  TrademarkSearchRequest,
  TrademarkSearchResult,
  TrademarkSearchResponse,
};

/**
 * Uploads a document to Cloudflare R2
 * @param file The file to upload
 * @param documentType The type of document
 * @returns The URL of the uploaded document
 */
export async function uploadDocumentToCloudflare(
  file: File,
  documentType: string
): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("documentType", documentType);

  const response = await fetch("/api/upload-document", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to upload document");
  }

  const data = await response.json();
  return data.url;
}
